import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { testSelectors } from '../../store/selectors/test';
import { authSelectors } from '../../store/selectors/auth';
import Button from '../../common/Buttons/Button';
import ErrorField from '../../common/Errors/ErrorField';
import actions from '../../store/actions';
import { continueTestThunk } from '../../store/thunks/test';
import { checkTest } from '../../api/test';
import useSpeechRecognition, { playVerdictSound, primeVerdictSound } from '../../hooks/useSpeechRecognition';
import useSpeech from '../../hooks/useSpeech';
import { leaveTestStage } from '../../utils/testStage';
import { isMuted } from '../../utils/sounds';
import './test-speak.scss';

const MAX_ATTEMPTS = 5;
const NEXT_DELAY_MS = 500;
const AUTO_SPEAK_DELAY_MS = 350;
const UNSUPPORTED_TEXT = 'Цей браузер не вміє розпізнавати мовлення. Відкрийте кабінет у Chrome, Edge або Safari — або пропустіть слово.';
const SAVE_ERROR_TEXT = 'Не вдалося зберегти результат. Перевірте інтернет і спробуйте ще раз.';

const MicIcon = () => (
  <svg viewBox="0 0 24 24" width="40" height="40" aria-hidden="true">
    <path d="M12 15a4 4 0 0 0 4-4V6a4 4 0 0 0-8 0v5a4 4 0 0 0 4 4Z" fill="currentColor" />
    <path d="M19 11a7 7 0 0 1-14 0M12 18v3M9 21h6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const TestSpeak = ({ setOpenModal }) => {
  const dispatch = useDispatch();
  const auth = useSelector(authSelectors);
  const token = auth.token;
  const test = useSelector(testSelectors);
  const testInfo = test.test;
  const loadBtn = test.loadBtn;

  const recognition = useSpeechRecognition(testInfo.tts_lang);
  const speech = useSpeech(testInfo.tts_lang);
  const autoSpeakRef = useRef(null);
  const [autoSpeakTask, setAutoSpeakTask] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [heard, setHeard] = useState('');
  const [saveError, setSaveError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const timerRef = useRef(null);
  const taskRef = useRef(null);

  useEffect(() => {
    clearTimeout(timerRef.current);
    recognition.stop();
    setAttempts(0);
    setStatus('idle');
    setMessage('');
    setHeard('');
    setSaveError('');
    setIsSaving(false);
    taskRef.current = testInfo.homework_task_id;
    dispatch(actions.test.checkTest({}));
    dispatch(actions.test.getTestError(''));
    clearTimeout(autoSpeakRef.current);
    speech.stop();
    setAutoSpeakTask(null);
    if (testInfo.homework_task_name) {
      const taskId = testInfo.homework_task_id;
      autoSpeakRef.current = setTimeout(() => setAutoSpeakTask(taskId), AUTO_SPEAK_DELAY_MS);
    }
  }, [testInfo.homework_task_id]);

  const voiceReady = speech.voicesLoaded && (Boolean(speech.voiceName) || !speech.hasVoice);

  useEffect(() => {
    if (autoSpeakTask === null || !voiceReady) return;
    setAutoSpeakTask(null);
    if (autoSpeakTask === taskRef.current && !isMuted()) {
      speech.speakText(testInfo.homework_task_name, { id: 'auto-speak' });
    }
  }, [autoSpeakTask, voiceReady]);

  useEffect(() => () => {
    clearTimeout(timerRef.current);
    clearTimeout(autoSpeakRef.current);
    speech.stop();
  }, []);

  const goNext = () => {
    const id = testInfo.el_homework_block_id;
    leaveTestStage();
    localStorage.setItem('currentTestId', JSON.stringify(id));
    dispatch(continueTestThunk({ id, token }));
  };

  const saveResult = async (payload) => {
    setIsSaving(true);
    setSaveError('');
    try {
      await checkTest({
        blockId: testInfo.el_homework_block_id,
        taskId: testInfo.homework_task_id,
        data: payload,
        token,
      });
      return true;
    } catch (error) {
      setSaveError(error?.response?.data?.info?.message || SAVE_ERROR_TEXT);
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const handlePassed = async (heardText, usedAttempts) => {
    setStatus('passed');
    setMessage('Чудово!');
    if (!isMuted()) playVerdictSound('ok');
    const saved = await saveResult({ heard: heardText, attempts: usedAttempts, skipped: false });
    if (!saved || taskRef.current !== testInfo.homework_task_id) return;
    timerRef.current = setTimeout(goNext, NEXT_DELAY_MS);
  };

  const handleListen = async () => {
    if (status === 'listening' || status === 'passed' || isSaving) return;
    if (!recognition.isSupported) {
      setStatus('failed');
      setMessage(UNSUPPORTED_TEXT);
      setAttempts(MAX_ATTEMPTS);
      return;
    }
    primeVerdictSound();
    clearTimeout(autoSpeakRef.current);
    speech.stop();
    setStatus('listening');
    setMessage('');
    setHeard('');
    const result = await recognition.listen({ id: testInfo.homework_task_id, word: testInfo.homework_task_name });
    if (taskRef.current !== testInfo.homework_task_id) return;

    const used = attempts + 1;
    setAttempts(used);
    setHeard(result.heard || '');

    if (result.verdict === 'ok' || result.verdict === 'close') {
      handlePassed(result.heard, used);
      return;
    }

    setStatus('failed');
    if (!isMuted()) playVerdictSound('wrong');
    if (result.verdict === 'wrong') {
      setMessage(used >= MAX_ATTEMPTS ? 'Не вийшло. Можна спробувати ще або пропустити слово.' : 'Не те слово. Спробуйте ще раз.');
    } else {
      setMessage(result.message || 'Не вдалося розпізнати. Спробуйте ще раз.');
    }
  };

  const handleSkip = async () => {
    if (isSaving || status === 'passed') return;
    recognition.stop();
    setStatus('passed');
    setMessage('Слово пропущено і зараховано.');
    const saved = await saveResult({ heard: heard || '', attempts: Math.max(attempts, 1), skipped: true });
    if (!saved || taskRef.current !== testInfo.homework_task_id) return;
    timerRef.current = setTimeout(goNext, NEXT_DELAY_MS);
  };

  const retrySave = () => {
    if (status !== 'passed') return;
    const skipped = message.startsWith('Слово пропущено');
    saveResult({ heard: heard || '', attempts: Math.max(attempts, 1), skipped }).then((saved) => {
      if (saved && taskRef.current === testInfo.homework_task_id) timerRef.current = setTimeout(goNext, NEXT_DELAY_MS);
    });
  };

  const canSkip = attempts >= MAX_ATTEMPTS && status !== 'passed';
  const isListening = status === 'listening';
  const levelBars = [0.15, 0.35, 0.55, 0.75, 0.95];

  return (
    <>
      <div className="test__field test-speak">
        {testInfo.homework_task_hint && <div className="test-speak__hint">{testInfo.homework_task_hint}</div>}

        <button
          type="button"
          className={`test-speak__mic test-speak__mic--${status}`}
          onClick={handleListen}
          disabled={status === 'passed' || isSaving}
          aria-label="Сказати слово"
        >
          <MicIcon />
        </button>

        <div className="test-speak__state">
          {isListening && !recognition.isMicOn && 'Вмикаємо мікрофон…'}
          {isListening && recognition.isMicOn && (
            <span className="test-speak__listening">
              Слухаємо… скажіть слово
              <span className="test-speak__level">
                {levelBars.map((threshold) => (
                  <i key={threshold} className={recognition.level >= threshold ? 'is-on' : ''} />
                ))}
              </span>
            </span>
          )}
          {status === 'idle' && 'Натисніть на мікрофон і вимовте слово'}
          {status === 'passed' && <span className="test-speak__ok">{message}</span>}
          {status === 'failed' && <span className="test-speak__bad">{message}</span>}
        </div>

        {status === 'failed' && heard && (
          <div className="test-speak__heard">Ми почули: <b>{heard}</b></div>
        )}

        <div className="test-speak__tries" aria-label={`Спроба ${Math.min(attempts, MAX_ATTEMPTS)} з ${MAX_ATTEMPTS}`}>
          {Array.from({ length: MAX_ATTEMPTS }, (_, index) => (
            <i key={index} className={index < attempts ? 'is-used' : ''} />
          ))}
        </div>

        {saveError && (
          <div className="test-speak__save-error">
            <ErrorField errorText={saveError} />
            <Button buttonBg="green" textSize="md" btnRadius="md" onClick={retrySave} disabled={isSaving}>Спробувати зберегти ще раз</Button>
          </div>
        )}
      </div>
      <div className="test__btns">
        {canSkip && (
          <Button buttonBg="green" textSize="md" btnRadius="md" disabled={loadBtn || isSaving} onClick={handleSkip}>
            Пропустити
          </Button>
        )}
        <Button buttonBg="light" iconSvg="flag" iconColor="violet" onClick={() => setOpenModal(true)}>
          Повідомити про помилку
        </Button>
      </div>
    </>
  );
};

TestSpeak.propTypes = {
  setOpenModal: PropTypes.func,
};

export default TestSpeak;
